'use client'

import type { UseQueryResult } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { RefreshCw, Search } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
} from '@ui/web/components'
import { LeaderboardIcon, MissionIcon, PuzzleIcon, ScoreboardIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { CourseMenuItem } from '../../components/cards'
import { ContinueLessonCard } from '../../components/cards'
import { EditCourseDialog } from '../edit-course'

type TeacherCourse = RouterOutputs['lesson']['listTeacherCourses']['courses'][number]

export interface MyCoursesProps {
  type: 'school' | 'web'
  coursesQuery: UseQueryResult<RouterOutputs['lesson']['listTeacherCourses'], unknown>
  onCourseClick: (courseId: string) => void
  buildMenuAction: (courseId: string, path: string) => () => void
  className?: string
}

const ENDED_DAYS_OPTIONS = [
  { label: '7 hari terakhir', value: 7 },
  { label: '30 hari terakhir', value: 30 },
  { label: '90 hari terakhir', value: 90 },
  { label: '1 tahun terakhir', value: 365 },
]

function classifyCourse(course: TeacherCourse, now: Date): 'active' | 'scheduled' | 'ended' {
  const { startDate, endDate } = course
  if (endDate && endDate < now) return 'ended'
  if (startDate && startDate > now) return 'scheduled'
  return 'active'
}

function buildMenuItems(
  courseId: string,
  buildMenuAction: (courseId: string, path: string) => () => void,
): CourseMenuItem[] {
  return [
    {
      label: 'Papan Peringkat',
      icon: <LeaderboardIcon size={16} />,
      onClick: buildMenuAction(courseId, '/belajar/t/peringkat'),
    },
    {
      label: 'Penilaian',
      icon: <ScoreboardIcon size={16} />,
      onClick: buildMenuAction(courseId, '/belajar/t/penilaian'),
    },
    {
      label: 'Misi',
      icon: <MissionIcon size={16} />,
      onClick: buildMenuAction(courseId, '/belajar/t/misi'),
    },
    {
      label: 'Tugas',
      icon: <PuzzleIcon size={16} />,
      onClick: buildMenuAction(courseId, '/belajar/t/overview'),
    },
  ]
}

export function MyCourses({ type, coursesQuery, onCourseClick, buildMenuAction, className }: MyCoursesProps) {
  const [endedDays, setEndedDays] = useState(30)
  const [endedSearch, setEndedSearch] = useState('')
  const [editingCourse, setEditingCourse] = useState<TeacherCourse | null>(null)

  const { active, scheduled, ended } = useMemo(() => {
    if (!coursesQuery.data) return { active: [], scheduled: [], ended: [] }

    const now = new Date()
    const cutoff = new Date(now.getTime() - endedDays * 24 * 60 * 60 * 1000)
    const a: TeacherCourse[] = []
    const s: TeacherCourse[] = []
    const e: TeacherCourse[] = []

    for (const course of coursesQuery.data.courses) {
      const kind = classifyCourse(course, now)
      if (kind === 'active') a.push(course)
      else if (kind === 'scheduled') s.push(course)
      else if (!course.endDate || course.endDate >= cutoff) e.push(course)
    }

    const searchLower = endedSearch.toLowerCase().trim()
    const filteredEnded = searchLower ? e.filter((c) => c.title.toLowerCase().includes(searchLower)) : e

    return { active: a, scheduled: s, ended: filteredEnded, now }
  }, [coursesQuery.data, endedDays, endedSearch])

  if (coursesQuery.isPending) {
    return (
      <div className={cn('srd:flex srd:flex-col srd:gap-y-6 srd:px-4', className)}>
        <CourseSectionSkeleton />
        <CourseSectionSkeleton />
      </div>
    )
  }

  if (coursesQuery.isError) {
    return (
      <div
        className={cn(
          'srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-3 srd:py-16 srd:text-center',
          className,
        )}
      >
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
          Gagal memuat tugas belajar
        </p>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void coursesQuery.refetch()}
          disabled={coursesQuery.isRefetching}
          className='srd:gap-x-2'
        >
          <RefreshCw className={cn('srd:size-3.5', coursesQuery.isRefetching && 'srd:animate-spin')} />
          Coba lagi
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className={cn('srd:flex srd:flex-col srd:gap-y-8', className)}>
        <h2 className={cn(typographyVariants({ variant: 'title-md' }), 'srd:px-4')}>Tugas Saya</h2>

        <CourseSection
          label='Aktif'
          courses={active}
          onCourseClick={onCourseClick}
          buildMenuAction={buildMenuAction}
          onSettingsClick={setEditingCourse}
          emptyMessage='Belum ada tugas aktif'
          className='srd:px-4'
        />

        <CourseSection
          label='Terjadwal'
          courses={scheduled}
          onCourseClick={onCourseClick}
          buildMenuAction={buildMenuAction}
          onSettingsClick={setEditingCourse}
          emptyMessage='Belum ada tugas terjadwal'
          className='srd:px-4'
        />

        <Separator />

        {/* Ended — date filter + search */}
        <div className='srd:flex srd:flex-col srd:gap-y-4 srd:px-4'>
          <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-x-3 srd:gap-y-2'>
            <p className={typographyVariants({ variant: 'title-sm' })}>Berakhir ({ended.length})</p>

            <Select value={String(endedDays)} onValueChange={(v) => setEndedDays(Number(v))}>
              <SelectTrigger size='sm' className='srd:w-fit srd:min-w-24'>
                <SelectValue>{ENDED_DAYS_OPTIONS.find((opt) => opt.value === endedDays)?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ENDED_DAYS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <InputGroup size='sm' className='srd:ml-auto srd:max-w-xs'>
              <InputGroupAddon align='inline-start'>
                <Search />
              </InputGroupAddon>
              <InputGroupInput
                value={endedSearch}
                onChange={(e) => setEndedSearch(e.target.value)}
                placeholder='Cari di tugasku'
              />
            </InputGroup>
          </div>

          {ended.length === 0 ? (
            <p
              className={cn(
                typographyVariants({ variant: 'body-lg' }),
                'srd:py-8 srd:text-center srd:text-neutral-black',
              )}
            >
              {endedSearch ? 'Tugas tidak ditemukan' : 'Belum ada tugas berakhir'}
            </p>
          ) : (
            <div className='srd:flex srd:flex-wrap srd:gap-3'>
              {ended.map((course) => (
                <ContinueLessonCard
                  key={course.id}
                  id={course.id}
                  title={course.title}
                  coverUrl={course.coverUrl}
                  size='small'
                  variant='course'
                  action={{ type: 'callback', onClick: () => onCourseClick(course.id) }}
                  menuItems={buildMenuItems(course.id, buildMenuAction)}
                  onSettingsClick={() => setEditingCourse(course)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {editingCourse && (
        <EditCourseDialog
          type={type}
          open={true}
          onOpenChange={(v) => {
            if (!v) setEditingCourse(null)
          }}
          courseId={editingCourse.id}
          initialTitle={editingCourse.title}
          initialStartDate={editingCourse.startDate}
          initialEndDate={editingCourse.endDate}
          initialCreatedAt={editingCourse.createdAt}
          initialParticipantIds={editingCourse.childIds}
          readOnly={false}
          onSuccess={() => {
            setEditingCourse(null)
            void coursesQuery.refetch()
          }}
          onScheduleSuccess={() => void coursesQuery.refetch()}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

function CourseSection({
  label,
  courses,
  onCourseClick,
  buildMenuAction,
  onSettingsClick,
  emptyMessage,
  className,
}: {
  label: string
  courses: TeacherCourse[]
  onCourseClick: (courseId: string) => void
  buildMenuAction: (courseId: string, path: string) => () => void
  onSettingsClick: (course: TeacherCourse) => void
  emptyMessage: string
  className?: string
}) {
  return (
    <div className={cn('srd:flex srd:flex-col srd:gap-y-3', className)}>
      <p className={typographyVariants({ variant: 'title-sm' })}>
        {label} ({courses.length})
      </p>

      {courses.length === 0 ? (
        <p
          className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:py-6 srd:text-center srd:text-neutral-black')}
        >
          {emptyMessage}
        </p>
      ) : (
        <div className='srd:flex srd:flex-wrap srd:gap-3'>
          {courses.map((course) => (
            <ContinueLessonCard
              key={course.id}
              id={course.id}
              title={course.title}
              coverUrl={course.coverUrl}
              size='small'
              variant='course'
              action={{ type: 'callback', onClick: () => onCourseClick(course.id) }}
              menuItems={buildMenuItems(course.id, buildMenuAction)}
              onSettingsClick={() => onSettingsClick(course)}
              className='srd:w-fit'
            />
          ))}
        </div>
      )}
    </div>
  )
}

function CourseSectionSkeleton() {
  return (
    <div className='srd:flex srd:flex-col srd:gap-y-3'>
      <Skeleton className='srd:h-5 srd:w-24' />
      <div className='srd:flex srd:gap-3 srd:flex-wrap'>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className='srd:h-[220px] srd:w-[235px] srd:rounded-2xl' />
        ))}
      </div>
    </div>
  )
}
